import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Stamp, Upload, CheckCircle2, Loader2, Trash2 } from 'lucide-react';

export default function CompanyStampSettingsCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);
  const [previewKey, setPreviewKey] = useState(Date.now());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/company/stamp/status');
      setStatus(r.data || {});
    } catch (e) {
      toast.error("Impossible de charger l'état du tampon");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Format image requis (PNG, JPG, WEBP)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/company/stamp', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Tampon importé');
      setPreviewKey(Date.now());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'import");
    }
    setUploading(false);
  };

  const remove = async () => {
    if (!window.confirm('Supprimer le tampon ? Il ne sera plus apposé sur les PV de réception.')) return;
    setDeleting(true);
    try {
      await api.delete('/company/stamp');
      toast.success('Tampon supprimé');
      await load();
    } catch (e) {
      toast.error('Échec de la suppression');
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="text-slate-500 text-sm">Chargement…</span>
      </div>
    );
  }

  const isUp = !!status?.uploaded_at;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
          <Stamp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Tampon d'entreprise</h2>
          <p className="text-sm text-slate-500">{"Apposé automatiquement dans la case « Signature & cachet » des procès-verbaux de réception."}</p>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-5 flex-wrap">
        <div className={`w-48 h-32 rounded-lg border-2 flex items-center justify-center bg-slate-50 ${isUp ? 'border-emerald-300' : 'border-dashed border-slate-300'}`}>
          {isUp ? (
            <img
              key={previewKey}
              src={`${apiBase}/company/stamp/preview?_=${previewKey}`}
              alt="Tampon"
              className="max-w-full max-h-full object-contain p-2"
            />
          ) : (
            <div className="text-center text-slate-400 text-xs px-3">
              <Stamp className="w-8 h-8 mx-auto mb-1 opacity-50" />
              Aucun tampon
            </div>
          )}
        </div>

        <div className="flex-1 min-w-[200px]">
          {isUp ? (
            <div className="flex items-center gap-2 text-emerald-700 text-sm mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>Tampon actif — {status.filename}</span>
            </div>
          ) : (
            <p className="text-sm text-slate-600 mb-2">Importez une image PNG/JPG (fond transparent recommandé) — max 5 Mo.</p>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { upload(e.target.files?.[0]); try { e.target.value = ''; } catch (_) { /* noop */ } }}
            data-testid="stamp-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-stamp-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {isUp ? 'Remplacer' : 'Importer le tampon'}
            </Button>
            {isUp && (
              <Button
                size="sm"
                variant="outline"
                onClick={remove}
                disabled={deleting}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                data-testid="delete-stamp-btn"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                Supprimer
              </Button>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            {"Astuce : un PNG transparent rendra mieux qu'un JPG sur fond blanc. Évitez les tampons trop sombres pour préserver la lisibilité."}
          </p>
        </div>
      </div>
    </div>
  );
}
